// The wizard's Deployment stage — the PM side of the page-build loop
// (docs/contentiq-deployment-extension.md, backend routers/page_builds.py).
// The wizard owns the deploy data (gate/builds/rollup) and passes it down;
// this panel renders it and drives the PM actions: assign the page set to a
// supplier, review submitted URLs, request corrections, accept.
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  BRAND, FONTS, INK, NEUTRAL, HUES, STATE, RADII, card, displayFont, pill,
  buttonDisabled, buttonLink, buttonPrimary, buttonSmall, buttonPrimarySmall,
} from '../../utils/wizardTheme';
import { sanitizeCopyHtml } from '../../utils/richCopy';
import {
  IN_REVIEW_STATUSES, ballNames, buildPageNote, buildPageTitle, isEdmBuild, packageSteps, packageZipName,
  splitFormFields,
} from '../../utils/packageStatus';
import { BUILD_STATUS_LABELS, BUILD_STATUS_PILL, failedChecks, reportChecks } from '../../utils/ciqBuildStatus';
import CiqStageChip from '../../components/CiqStageChip';
import CiqBallChips from '../../components/CiqBallChips';
import CiqEdmCopy, { EDM_FIELDS } from '../../components/CiqEdmCopy';
import CiqFollowUpEmails from '../../components/CiqFollowUpEmails';
import CiqPackageEvents from '../../components/CiqPackageEvents';
import { fmtDate } from '../../components/ZbSummary';
import { downloadFileBlob } from '../../utils/ciqAssets';
import { useCurrentUser } from '../../context/UserContext';

const API = process.env.REACT_APP_API_URL;

// ---------------------------------------------------------------------------
// Build ingredients (Phase B round 2, Edward 2026-10-22): the PM may not
// have brand material while gathering, so Page build must be able to say
// "I don't have this — please give it to me." Reads the format's image
// manifest + the client brand through GET /build-readiness; anything the
// client brand already holds shows as satisfied, never re-asked. Informational
// only — it never blocks the assign (brand material often arrives late).
// ---------------------------------------------------------------------------
function IngredientRow({ ok, label, detail, action }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', fontSize: '12.5px' }}>
      <span style={{ color: ok ? STATE.done.text : STATE.gate.text, fontWeight: 700, flexShrink: 0 }}>
        {ok ? '✓' : '!'}
      </span>
      <span style={{ color: INK.body, flexShrink: 0 }}>{label}</span>
      {detail && <span style={{ color: INK.muted, fontSize: '11.5px' }}>{detail}</span>}
      {action}
    </div>
  );
}

// refreshToken: re-read readiness when it changes (the parent's deploy payload,
// which reloads after brand saves, logo changes and attachments).
function BuildIngredientsCard({ projectId, onOpenBrand, onOpenQuestions, refreshToken }) {
  const [state, setState] = useState(null);
  const [err, setErr] = useState('');
  const [promptPkg, setPromptPkg] = useState(null);
  const [promptsOpen, setPromptsOpen] = useState(false);
  const [copiedKey, setCopiedKey] = useState(null);
  const [uploadingKey, setUploadingKey] = useState(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/contentiq/projects/${projectId}/build-readiness`);
      setState(r.data); setErr('');
    } catch { setErr('Could not load the build ingredients.'); }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, refreshToken]);

  const openPrompts = async () => {
    setPromptsOpen(o => !o);
    if (!promptPkg) {
      try {
        const r = await axios.get(`${API}/contentiq/projects/${projectId}/prompt-package`);
        setPromptPkg(r.data);
      } catch { setPromptPkg({ prompts: [], note: 'Could not load the prompt package.' }); }
    }
  };

  const copyPrompt = async (p) => {
    try {
      await navigator.clipboard.writeText(p.prompt);
      setCopiedKey(p.key);
      setTimeout(() => setCopiedKey(null), 2500);
    } catch { /* clipboard denied — the text is on screen to select */ }
  };

  const attach = async (imageClass, files) => {
    if (!files?.length) return;
    setUploadingKey(imageClass); setErr('');
    for (const f of files) {
      const fd = new FormData();
      fd.append('file', f);
      fd.append('kind', 'build_image');
      fd.append('image_class', imageClass);
      try {
        await axios.post(`${API}/contentiq/projects/${projectId}/assets`, fd);
      } catch (e) {
        setErr(e.response?.data?.detail || `Couldn't attach ${f.name}.`);
      }
    }
    setUploadingKey(null);
    await load();
  };

  if (!state) {
    return err
      ? <div style={{ fontSize: '12.5px', color: STATE.danger.text }}>{err}</div>
      : <div style={{ fontSize: '12.5px', color: INK.muted }}>Checking build ingredients…</div>;
  }

  const brandLink = onOpenBrand && (
    <button onClick={onOpenBrand} style={{ ...buttonLink, fontSize: '11.5px' }}>
      add it under Brand ↓
    </button>
  );
  const b = state.brand || {};
  const logoVia = b.logo?.via === 'client_brand' ? `from ${b.client_name || 'the client'}'s saved brand`
    : b.logo?.via === 'project_files' ? "from this project's brand files" : null;

  return (
    <div style={{ ...card, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap' }}>
        <div style={{ fontSize: '14px', fontWeight: 700, color: INK.primary }}>Build ingredients</div>
        {state.provisional && (
          <span style={pill(STATE.gate.text, STATE.gate.tint)}>image list not final for this product yet</span>
        )}
        {state.ready
          ? <span style={{ fontSize: '12px', color: STATE.done.text, fontWeight: 600 }}>✓ everything in hand</span>
          : <span style={{ fontSize: '12px', color: STATE.gate.text, fontWeight: 600 }}>missing: {state.missing.join(' · ')}</span>}
      </div>
      <IngredientRow ok={b.colors} label="Brand colors"
        detail={b.colors ? null : 'none saved for this client'}
        action={!b.colors && brandLink} />
      <IngredientRow ok={!!b.logo?.satisfied} label="Client logo"
        detail={logoVia || 'no logo on the client brand or this project'}
        action={!b.logo?.satisfied && brandLink} />
      <IngredientRow ok={b.legal_language} label="Required legal language"
        detail={b.legal_language ? null : 'optional unless the client mandates copy'}
        action={!b.legal_language && brandLink} />
      <IngredientRow ok={b.privacy_policy_url} label="Client privacy policy URL"
        detail={b.privacy_policy_url ? null : 'required before the pages can go to the build team'}
        action={!b.privacy_policy_url && onOpenQuestions && (
          <button onClick={onOpenQuestions} style={{ ...buttonLink, fontSize: '11.5px' }}>
            add it on Questions &amp; privacy →
          </button>
        )} />
      {state.has_manifest && (state.images || []).filter(i => i.key !== 'client_logo').map(img => (
        <div key={img.key} style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
          <IngredientRow ok={img.missing === 0}
            label={img.label}
            detail={`${img.spec} · ${img.attached.length} of ${img.required} attached`}
            action={(
              <label style={{ color: HUES.strategy.text, fontSize: '11.5px', cursor: 'pointer', textDecoration: 'underline' }}>
                {uploadingKey === img.key ? 'attaching…' : '📎 attach'}
                <input type="file" multiple accept=".png,.jpg,.jpeg,.gif,.svg" style={{ display: 'none' }}
                  onChange={e => { attach(img.key, Array.from(e.target.files || [])); e.target.value = ''; }} />
              </label>
            )} />
          {img.attached.length > 0 && (
            <div style={{ fontSize: '11.5px', color: INK.muted, paddingLeft: '18px' }}>
              {img.attached.map(a => a.filename).join(' · ')}
            </div>
          )}
        </div>
      ))}
      {!state.has_manifest && (
        <div style={{ fontSize: '12px', color: INK.muted }}>
          This product&rsquo;s image list isn&rsquo;t set up yet — image needs show here once it is.
        </div>
      )}
      <div>
        <button onClick={openPrompts}
          style={buttonLink}>
          {promptsOpen ? '▾' : '▸'} AI prompt package — pre-filled prompts for the images above
        </button>
        {promptsOpen && promptPkg && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '8px' }}>
            {(promptPkg.prompts || []).map(p => (
              <div key={p.key} style={{ background: NEUTRAL.chipBg, borderRadius: RADII.small, padding: '8px 10px' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                  <span style={{ fontSize: '12px', fontWeight: 700, color: INK.primary }}>{p.label}</span>
                  <span style={{ fontSize: '11px', color: INK.muted }}>{p.spec}</span>
                  <button onClick={() => copyPrompt(p)} style={{ ...buttonSmall, padding: '2px 8px', marginLeft: 'auto' }}>
                    {copiedKey === p.key ? '✓ copied' : 'Copy'}
                  </button>
                </div>
                <div style={{ fontSize: '11.5px', color: INK.body, lineHeight: 1.5, marginTop: '4px', whiteSpace: 'pre-wrap' }}>{p.prompt}</div>
              </div>
            ))}
            {promptPkg.note && (
              <div style={{ fontSize: '11.5px', color: INK.muted, lineHeight: 1.5 }}>{promptPkg.note}</div>
            )}
          </div>
        )}
      </div>
      {err && <div style={{ fontSize: '12.5px', color: STATE.danger.text }}>{err}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The build package (Phase B round 3 — Edward approved the mocks 2026-10-22).
// Unsent: the draft form — build team, needed-by date, note, the optional
// promotion email — and Send (missing images/brand ride as "to follow";
// only the copy gate can refuse). Sent: who has the ball, the progress
// strip, what each version carried, a revised send, and the activity log.
// The page-by-page review stays in the cards below this panel.
// ---------------------------------------------------------------------------
function EdmCopyFields({ value, onChange }) {
  const v = value || {};
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      {EDM_FIELDS.map(([key, label, rows]) => (
        <label key={key} style={{ fontSize: '12px', color: INK.secondary }}>
          {label}
          {rows > 1 ? (
            <textarea rows={rows} value={v[key] || ''} onChange={e => onChange({ ...v, [key]: e.target.value })}
              style={{ display: 'block', width: '100%', boxSizing: 'border-box', marginTop: '3px', padding: '7px 9px', fontSize: '12.5px', fontFamily: FONTS.body, border: `1px solid ${NEUTRAL.controlSoft}`, borderRadius: RADII.small, resize: 'vertical' }} />
          ) : (
            <input value={v[key] || ''} onChange={e => onChange({ ...v, [key]: e.target.value })}
              style={{ display: 'block', width: '100%', boxSizing: 'border-box', marginTop: '3px', padding: '7px 9px', fontSize: '12.5px', fontFamily: FONTS.body, border: `1px solid ${NEUTRAL.controlSoft}`, borderRadius: RADII.small }} />
          )}
        </label>
      ))}
    </div>
  );
}

// refreshToken: the parent's deploy payload — a new one means a page changed
// (accepted, corrections, launch), so the ball is re-read with it.
function PackagePanel({ projectId, onReload, refreshToken }) {
  const { currentUser } = useCurrentUser();
  const [data, setData] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [revising, setRevising] = useState(false);
  const [revision, setRevision] = useState({ change_note: '', include_edm: false, edm_copy: {} });

  const load = async () => {
    try {
      const r = await axios.get(`${API}/contentiq/projects/${projectId}/package`);
      setData(r.data);
      const p = r.data.package;
      setForm({ supplier_id: p.supplier_id || '', needed_by: p.needed_by || '', note: p.note || '',
                include_edm: !!p.include_edm, edm_copy: p.edm_copy || {} });
      setErr('');
    } catch { setErr('Could not load the package.'); }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, refreshToken]);
  useEffect(() => {
    axios.get(`${API}/suppliers/`).then(r => setSuppliers(r.data || [])).catch(() => setSuppliers([]));
  }, []);

  if (!data || !form) {
    return err ? <div style={{ fontSize: '12.5px', color: STATE.danger.text }}>{err}</div> : null;
  }
  const p = data.package;
  const state = data.state || {};
  const names = ballNames(data, { currentUserId: currentUser?.id });
  const sent = p.version >= 1;
  const toFollow = data.readiness?.missing || [];

  const payload = () => ({
    supplier_id: form.supplier_id ? Number(form.supplier_id) : null,
    needed_by: form.needed_by || null,
    note: form.note || null,
    include_edm: !!form.include_edm,
    edm_copy: form.edm_copy,
  });

  const saveDraft = async () => {
    setBusy(true); setErr('');
    try { await axios.put(`${API}/contentiq/projects/${projectId}/package`, payload()); await load(); }
    catch (e) { setErr(e.response?.data?.detail || 'Could not save the draft.'); }
    finally { setBusy(false); }
  };

  const send = async () => {
    if (!form.supplier_id) { setErr('Pick the build team to send it to.'); return; }
    const team = suppliers.find(x => String(x.id) === String(form.supplier_id))?.supplier_name || 'the build team';
    if (!window.suppressConfirm && !window.confirm(
      `Send the package to ${team}? They are emailed now, and each page's copy is frozen as sent.`)) return;
    setBusy(true); setErr('');
    try {
      await axios.post(`${API}/contentiq/projects/${projectId}/package/send`, payload());
      await load();
      await onReload();
    } catch (e) { setErr(e.response?.data?.detail || 'Could not send the package.'); }
    finally { setBusy(false); }
  };

  const openRevise = () => {
    setRevision({ change_note: '', include_edm: !!p.include_edm, edm_copy: p.edm_copy || {} });
    setRevising(true);
  };

  const sendRevision = async () => {
    if (!revision.change_note.trim()) { setErr('Say what changed — the build team sees this note.'); return; }
    setBusy(true); setErr('');
    try {
      await axios.post(`${API}/contentiq/projects/${projectId}/package/revise`, revision);
      setRevising(false);
      await load();
      await onReload();
    } catch (e) { setErr(e.response?.data?.detail || 'Could not send the revised package.'); }
    finally { setBusy(false); }
  };

  const download = async () => {
    setErr('');
    try {
      await downloadFileBlob(`${API}/contentiq/packages/${p.id}/download`, packageZipName(data.project?.name, p.version));
    } catch { setErr('Could not download the package.'); }
  };

  const labelStyle = { fontSize: '12px', color: INK.secondary, display: 'flex', flexDirection: 'column', gap: '3px' };
  const inputStyle = { padding: '8px 10px', fontSize: '13px', fontFamily: FONTS.body, border: `1px solid ${NEUTRAL.controlSoft}`, borderRadius: RADII.small, background: NEUTRAL.pageBg };

  if (!sent) {
    if (!data.gate?.ready) return null;  // the "not ready" card above covers it
    return (
      <div id="ciq-package-send" style={{ ...card, padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '12px', maxWidth: '760px' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap' }}>
          <div style={{ fontSize: '15px', fontWeight: 700, color: INK.primary }}>Send the package</div>
          <CiqBallChips ball={state.ball} perspective="pm" names={names} />
        </div>
        <div style={{ fontSize: '12.5px', color: INK.secondary, lineHeight: 1.55 }}>
          The copy is final. Sending freezes each page's copy, questions and opt-in language
          into its build and emails the build team.
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
          <label style={{ ...labelStyle, flex: '1 1 220px' }}>Build team
            <select value={form.supplier_id} onChange={e => setForm({ ...form, supplier_id: e.target.value })} style={inputStyle}>
              <option value="">— pick —</option>
              {suppliers.map(s => <option key={s.id} value={s.id}>{s.supplier_name}</option>)}
            </select>
          </label>
          <label style={{ ...labelStyle, flex: '0 1 180px' }}>Review links needed by
            <input type="date" value={form.needed_by} onChange={e => setForm({ ...form, needed_by: e.target.value })} style={inputStyle} />
          </label>
        </div>
        <label style={labelStyle}>Note to the build team (optional)
          <textarea rows={2} value={form.note} onChange={e => setForm({ ...form, note: e.target.value })}
            style={{ ...inputStyle, resize: 'vertical' }} />
        </label>
        <div style={{ borderTop: `1px solid ${NEUTRAL.borderSoft}`, paddingTop: '10px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13.5px', fontWeight: 600, color: INK.primary, cursor: 'pointer' }}>
            <input type="checkbox" checked={form.include_edm}
              onChange={e => setForm({ ...form, include_edm: e.target.checked })} style={{ width: '17px', height: '17px' }} />
            Include a promotion email
          </label>
          <div style={{ fontSize: '12px', color: INK.secondary, lineHeight: 1.5 }}>
            Optional — an email that promotes the landing page. When on, the build team
            builds and hosts it too and returns its link with the page links. (A 2-Touch
            pair&rsquo;s follow-up email is not this: it travels with its pair&rsquo;s page.)
          </div>
          {form.include_edm && <EdmCopyFields value={form.edm_copy} onChange={v => setForm({ ...form, edm_copy: v })} />}
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          <button onClick={send} disabled={busy} style={{ ...buttonPrimary(), ...(busy ? buttonDisabled : {}) }}>
            Send package
          </button>
          <button onClick={saveDraft} disabled={busy} style={{ ...buttonSmall, ...(busy ? buttonDisabled : {}) }}>Save draft</button>
        </div>
        {toFollow.length > 0 && (
          // "Send package — 3 to follow" didn't say what the 3 were (Edward,
          // 2026-10-28) — name them, and say what happens to them.
          <div style={{ fontSize: '12.5px', color: STATE.gate.text, background: STATE.gate.bg, border: `1px solid ${STATE.gate.border}`, borderRadius: RADII.small, padding: '8px 10px', lineHeight: 1.5 }}>
            <b>Not ready yet, so they go as &ldquo;to follow&rdquo;:</b> {toFollow.join(' · ')}.
            {' '}You can still send now — adding them later sends the build team an update.
          </div>
        )}
        {err && <div style={{ fontSize: '12.5px', color: STATE.danger.text }}>{err}</div>}
      </div>
    );
  }

  const steps = packageSteps(state.status, !!p.acknowledged_at);
  const contents = p.contents || {};
  // Packages recorded by the migration for pages sent before packages existed
  // have no snapshot — count what's live instead.
  const carriedPages = contents.pages
    ? contents.pages.filter(x => x.kind !== 'edm').length
    : (data.pages?.total || 0) - (data.pages?.has_edm ? 1 : 0);
  const carriedEdm = contents.pages ? !!contents.include_edm : !!data.pages?.has_edm;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxWidth: '980px' }}>
      <div style={{ background: BRAND.navy, color: NEUTRAL.pageBg, borderRadius: RADII.card, padding: '16px 20px', display: 'flex', flexWrap: 'wrap', gap: '14px', alignItems: 'center' }}>
        <div style={{ flex: '1 1 460px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', opacity: 0.8 }}>
            Package v{p.version} · {state.status_label}
          </div>
          <div style={{ ...displayFont('19px', 700), color: NEUTRAL.pageBg }}>{state.next_step}</div>
          <div style={{ fontSize: '12.5px', opacity: 0.85 }}>
            {p.supplier_name}
            {p.acknowledged_at
              ? (p.eta ? ` acknowledged · ETA ${fmtDate(p.eta)}` : ' acknowledged · no ETA set yet')
              : ' — not acknowledged yet'}
            {p.needed_by ? ` · needed by ${fmtDate(p.needed_by)}` : ''}
            {state.eta_passed ? ' · ETA passed' : ''}
          </div>
        </div>
        <div style={{ background: NEUTRAL.pageBg, borderRadius: RADII.small, padding: '6px 10px' }}>
          <CiqBallChips ball={state.ball} perspective="pm" names={names} />
        </div>
      </div>

      <ol aria-label="Package progress" style={{ listStyle: 'none', margin: 0, padding: '10px 14px', ...card, display: 'flex', flexWrap: 'wrap', gap: '6px 18px', fontSize: '12.5px' }}>
        {steps.map(s => (
          <li key={s.key} style={{ color: s.state === 'done' ? STATE.done.text : s.state === 'current' ? INK.primary : INK.secondary,
            fontWeight: s.state === 'done' || s.state === 'current' ? 700 : 400,
            borderBottom: s.state === 'current' ? `2px solid ${BRAND.button}` : 'none' }}>
            {s.state === 'done' ? '✓ ' : ''}{s.label}{s.state === 'skipped' ? ' (skipped)' : ''}
          </li>
        ))}
      </ol>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-start' }}>
        <div style={{ ...card, padding: '14px 16px', flex: '999 1 420px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap' }}>
            <div style={{ fontSize: '14px', fontWeight: 700 }}>What v{p.version} carried</div>
            <div style={{ flexGrow: 1 }} />
            <button onClick={download} style={buttonSmall}>⬇ Download package (.zip)</button>
            {state.status !== 'launched' && !revising && (
              <button onClick={openRevise} style={buttonSmall}>Send a revised package…</button>
            )}
          </div>
          <div style={{ fontSize: '12.5px', color: INK.body, lineHeight: 1.6 }}>
            {carriedPages} page{carriedPages === 1 ? '' : 's'}
            {carriedEdm ? ' + promotion email' : ''} · build guide · QA checklist · config file in the template's own fields
          </div>
          {(contents.to_follow || []).length > 0 && (
            <div style={{ fontSize: '12px', color: STATE.gate.text, background: STATE.gate.bg, border: `1px solid ${STATE.gate.border}`, borderRadius: RADII.small, padding: '7px 10px' }}>
              Marked "to follow" in this version: {contents.to_follow.join(' · ')}
            </div>
          )}
          {revising && (
            <div style={{ borderTop: `1px solid ${NEUTRAL.borderSoft}`, paddingTop: '10px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={labelStyle}>What changed — the build team sees this note
                <textarea rows={2} value={revision.change_note}
                  onChange={e => setRevision({ ...revision, change_note: e.target.value })}
                  style={{ ...inputStyle, resize: 'vertical' }} />
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
                <input type="checkbox" checked={revision.include_edm}
                  onChange={e => setRevision({ ...revision, include_edm: e.target.checked })} />
                Include a promotion email
              </label>
              {revision.include_edm && (
                <EdmCopyFields value={revision.edm_copy} onChange={v => setRevision({ ...revision, edm_copy: v })} />
              )}
              <div style={{ fontSize: '12px', color: INK.secondary }}>
                Page copy changes after the send go back to the build team as corrections on the page itself.
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={sendRevision} disabled={busy} style={{ ...buttonPrimarySmall(), ...(busy ? buttonDisabled : {}) }}>Send v{p.version + 1}</button>
                <button onClick={() => setRevising(false)} style={buttonSmall}>Cancel</button>
              </div>
            </div>
          )}
          {err && <div style={{ fontSize: '12.5px', color: STATE.danger.text }}>{err}</div>}
        </div>
        <div style={{ ...card, padding: '14px 16px', flex: '1 1 280px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div style={{ fontSize: '14px', fontWeight: 700 }}>Activity</div>
          <div style={{ maxHeight: '260px', overflowY: 'auto' }}>
            <CiqPackageEvents events={p.events} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The auto-check report (slice 3). Pass or fail only (Edward, 2026-10-22:
// "skipped is confusing"): a check with nothing to verify is left out; one
// that couldn't run (no readable text, renderer unavailable) is a fail marked
// `unverified` — "look at the page yourself" — so an unchecked page never
// reads as passed. Unverified fails never pre-tick a correction: the build
// team can't fix "we couldn't check". Reports stored before this change still
// say "skipped"; normalizeCheck reads them the same way.
// ---------------------------------------------------------------------------

const CHECK_ICON = {
  pass: ['✓', STATE.done.text],
  fail: ['✗', STATE.danger.text],
  unverified: ['!', STATE.gate.text],
};

function EvidenceList({ items }) {
  if (!items || !items.length) return null;
  const shown = items.slice(0, 5);
  return (
    <ul style={{ margin: '3px 0 0', paddingLeft: '18px', color: INK.secondary }}>
      {shown.map((it, i) => <li key={i} style={{ marginBottom: '1px' }}>{it}</li>)}
      {items.length > shown.length && <li>…and {items.length - shown.length} more</li>}
    </ul>
  );
}

export function CheckReport({ report, buildId }) {
  if (!report) return null;
  const checks = reportChecks(report);
  const passed = checks.filter(c => c.status === 'pass').length;
  const failed = checks.filter(c => c.status === 'fail').length;
  const unverified = checks.filter(c => c.unverified).length;
  const headline = `Auto-check: ${passed} passed · ${failed} failed`
    + (unverified ? ` (${unverified} couldn't be checked)` : '');
  // The renderer's full-page screenshot (report.screenshot, backend
  // page_render.py) — fetched as an authed blob; a plain href would 401.
  const openScreenshot = async () => {
    try {
      const r = await axios.get(`${API}/contentiq/builds/${buildId}/check-screenshot`,
        { responseType: 'blob' });
      window.open(URL.createObjectURL(r.data), '_blank');
    } catch (e) { /* the link only renders when the report says one exists */ }
  };
  return (
    <details open={failed > 0} style={{ fontSize: '12px' }}>
      <summary style={{ cursor: 'pointer', fontWeight: 600, color: failed > 0 ? STATE.danger.text : STATE.done.text }}>
        {headline}
        <span style={{ fontWeight: 400, color: INK.muted }}>
          {report.checked_at ? ` — ${fmtDate(report.checked_at)}` : ''}
        </span>
      </summary>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', marginTop: '6px', background: NEUTRAL.pageBg, border: `1px solid ${NEUTRAL.borderSoft}`, borderRadius: RADII.small, padding: '8px 12px' }}>
        {checks.map(c => {
          const [glyph, color] = c.unverified ? CHECK_ICON.unverified : (CHECK_ICON[c.status] || CHECK_ICON.fail);
          const ev = c.evidence || {};
          return (
            <div key={c.id} style={{ lineHeight: 1.45 }}>
              <span style={{ color, fontWeight: 700 }}>{glyph}</span>{' '}
              <span style={{ color: INK.primary, fontWeight: 600 }}>{c.label}</span>
              {c.detail && <span style={{ color: INK.secondary }}> — {c.detail}</span>}
              <EvidenceList items={ev.missing} />
              <EvidenceList items={ev.changed} />
              <EvidenceList items={ev.problems} />
            </div>
          );
        })}
        {buildId && report.screenshot?.s3_key && (
          <button onClick={openScreenshot}
            style={{ ...buttonLink, fontSize: '12px', alignSelf: 'flex-start' }}>
            📷 View the full-page screenshot ↗
          </button>
        )}
      </div>
    </details>
  );
}

function BuildStatusPill({ status }) {
  const [fg, bg] = BUILD_STATUS_PILL[status] || [INK.secondary, NEUTRAL.pageBg];
  return <span style={pill(fg, bg)}>{BUILD_STATUS_LABELS[status] || status}</span>;
}

function NotesThread({ notes, team }) {
  const [open, setOpen] = useState(false);
  if (!notes.length) return null;
  return (
    <div style={{ fontSize: '12px' }}>
      <button onClick={() => setOpen(o => !o)}
        style={{ ...buttonLink, fontSize: '12px' }}>
        {open ? '▾' : '▸'} Notes ({notes.length})
      </button>
      {open && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '6px' }}>
          {notes.map((n, i) => (
            <div key={i} style={{
              padding: '6px 10px', borderRadius: RADII.small, lineHeight: 1.5,
              background: n.side === 'pm' ? HUES.strategy.tint : NEUTRAL.pageBg,
              border: `1px solid ${NEUTRAL.borderSoft}`,
            }}>
              <span style={{ color: INK.muted }}>
                {n.side === 'pm' ? 'DemandArc' : (team || 'Build team')}{n.by_name ? ` · ${n.by_name}` : ''}
                {n.ts ? ` · ${fmtDate(n.ts)}` : ''} —{' '}
              </span>
              <span style={{ color: INK.primary, whiteSpace: 'pre-wrap' }}>{n.text}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function BuildRow({ b, onReload, launched, team }) {
  const teamName = team || 'the build team';
  const [correcting, setCorrecting] = useState(false);
  const [note, setNote] = useState('');
  // The correction composer pre-ticks every failed check (everything is a
  // failure — Edward, 2026-10-08); the PM unticks false alarms and adds
  // free-text on top. Selected failures compose into the note the supplier
  // sees.
  const [tickedFailures, setTickedFailures] = useState([]);
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(false);
  const [editingUrl, setEditingUrl] = useState(false);
  const [urlDraft, setUrlDraft] = useState('');
  const [err, setErr] = useState('');
  const spec = b.spec || {};
  const isEdm = isEdmBuild(b);
  const actionable = (b.status === 'url_submitted' || b.status === 'checked') && !launched;
  const canCorrect = (actionable || b.status === 'accepted') && !launched;
  // A wrong link is fixable in place — no corrections round-trip needed
  // (Edward, 2026-10-08). Accepted pages stay locked: reopen first.
  const canEditUrl = !!b.url && !launched && b.status !== 'accepted';
  const failures = failedChecks(b.check_report);

  const saveUrl = async () => {
    setBusy(true); setErr('');
    try {
      await axios.post(`${API}/contentiq/builds/${b.id}/url`, { url: urlDraft.trim() });
      setEditingUrl(false);
      await onReload();
    } catch (e) { setErr(e.response?.data?.detail || 'Could not update the URL.'); }
    finally { setBusy(false); }
  };

  const openComposer = () => {
    setTickedFailures(failures.map(f => f.id));
    setCorrecting(true);
  };
  const composedNote = () => {
    const lines = failures
      .filter(f => tickedFailures.includes(f.id))
      .map(f => `• ${f.label} — ${f.detail || 'see the check report'}`);
    const extra = note.trim();
    if (extra) lines.push(extra ? (lines.length ? `• ${extra}` : extra) : '');
    return lines.join('\n');
  };

  const accept = async () => {
    // Failed checks warn, then allow — the history notes it (Edward, 2026-10-23).
    if (failures.length && !window.suppressConfirm && !window.confirm(
      `${failures.length === 1 ? '1 check failed' : `${failures.length} checks failed`}:\n`
      + failures.map(f => `• ${f.label}${f.detail ? ` — ${String(f.detail).replace(/\.+$/, '')}` : ''}`).join('\n')
      + '\n\nAccept this page anyway? The history notes that it was accepted with failed checks.')) return;
    setBusy(true); setErr('');
    try { await axios.post(`${API}/contentiq/builds/${b.id}/accept`); await onReload(); }
    catch (e) { setErr(e.response?.data?.detail || 'Could not accept.'); }
    finally { setBusy(false); }
  };
  const runCheck = async () => {
    setChecking(true); setErr('');
    try { await axios.post(`${API}/contentiq/builds/${b.id}/run-check`); await onReload(); }
    catch (e) { setErr(e.response?.data?.detail || 'Could not run the check.'); }
    finally { setChecking(false); }
  };
  const sendCorrections = async () => {
    const text = composedNote();
    if (!text.trim()) { setErr('Tick at least one check finding or write a note — it is what the build team sees.'); return; }
    setBusy(true); setErr('');
    try {
      await axios.post(`${API}/contentiq/builds/${b.id}/request-corrections`, { note: text });
      setNote(''); setCorrecting(false);
      await onReload();
    } catch (e) { setErr(e.response?.data?.detail || 'Could not send corrections.'); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ ...card, padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <span style={{ ...displayFont('15px', 600) }}>{isEdm ? (spec.asset?.title || `Build ${b.id}`) : buildPageTitle(b)}</span>
        <span style={{ fontSize: '11.5px', color: INK.muted }}>
          {isEdm ? 'Email' : buildPageNote(spec)}
        </span>
        <div style={{ flexGrow: 1 }} />
        <BuildStatusPill status={b.status} />
      </div>

      {isEdm && <CiqEdmCopy edm={spec.edm} />}

      {b.url ? (
        <div style={{ fontSize: '12.5px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <a href={b.url} target="_blank" rel="noreferrer" style={{ color: HUES.strategy.text, wordBreak: 'break-all' }}>
            {b.url}
          </a>
          {b.url_submitted_at && (
            <span style={{ color: INK.muted }}> — submitted {fmtDate(b.url_submitted_at)}</span>
          )}
          {canEditUrl && !editingUrl && (
            <button onClick={() => { setUrlDraft(b.url); setEditingUrl(true); }}
              title="Fix a wrong link in place — the auto-check re-runs on the new URL"
              style={{ ...buttonLink, fontSize: '12px' }}>
              ✎ Edit link
            </button>
          )}
        </div>
      ) : (
        <div style={{ fontSize: '12.5px', color: INK.muted }}>
          {isEdm ? `No link yet — ${teamName} is building the email.` : `No URL yet — ${teamName} is building this page.`}
        </div>
      )}

      {editingUrl && (
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <input value={urlDraft} onChange={e => setUrlDraft(e.target.value)} placeholder="https://…"
            style={{ flexGrow: 1, minWidth: '260px', padding: '7px 10px', fontSize: '12.5px', fontFamily: FONTS.body, border: `1px solid ${NEUTRAL.controlSoft}`, borderRadius: RADII.small }} />
          <button onClick={saveUrl} disabled={busy || !urlDraft.trim()} style={{ ...buttonPrimarySmall(), ...(busy || !urlDraft.trim() ? buttonDisabled : {}) }}>Save link</button>
          <button onClick={() => { setEditingUrl(false); setErr(''); }} style={buttonSmall}>Cancel</button>
        </div>
      )}

      {b.check_report && <CheckReport report={b.check_report} buildId={b.id} />}
      {actionable && !b.check_report && (
        <div style={{ fontSize: '12px', color: INK.secondary, background: NEUTRAL.pageBg, border: `1px solid ${NEUTRAL.borderSoft}`, borderRadius: RADII.small, padding: '6px 10px' }}>
          The automatic check runs right after submission — if nothing appears in a
          moment, use ↻ Run check below.
        </div>
      )}

      <NotesThread notes={b.notes || []} team={team} />

      {spec.html_title && !isEdm && (
        <div style={{ fontSize: '12px', color: INK.secondary }}>
          Page &lt;title&gt;: <b style={{ color: INK.primary }}>{spec.html_title}</b>
        </div>
      )}

      {(spec.compliance?.optin_language || (spec.compliance?.privacy_links || []).length > 0) && (
        <div style={{ fontSize: '12px', color: INK.secondary, lineHeight: 1.5 }}>
          <span style={{ fontWeight: 600 }}>Opt-in &amp; privacy as sent:</span>{' '}
          {spec.compliance?.optin_proposed && (
            <span style={{ ...pill(STATE.gate.text, STATE.gate.tint), marginRight: '6px' }}>Proposed — confirm with the client</span>
          )}
          {spec.compliance?.optin_language && (spec.compliance?.optin_language_html ? (
            <span style={{ color: INK.primary, display: 'inline-block' }}
              dangerouslySetInnerHTML={{ __html: sanitizeCopyHtml(spec.compliance.optin_language_html) }} />
          ) : (
            <span style={{ color: INK.primary, whiteSpace: 'pre-wrap' }}>{spec.compliance.optin_language}</span>
          ))}
          {(spec.compliance?.privacy_links || []).map((l, i) => (
            <span key={i}>
              {i > 0 || spec.compliance?.optin_language ? ' · ' : ''}
              <a href={l.url} target="_blank" rel="noreferrer" style={{ color: HUES.strategy.text, wordBreak: 'break-all' }}>
                {l.label || l.url}
              </a>
            </span>
          ))}
        </div>
      )}

      {(spec.follow_up_emails || []).length > 0 && <CiqFollowUpEmails emails={spec.follow_up_emails} />}

      {spec.copy_of_record && (
        <details style={{ fontSize: '12px', color: INK.secondary }}>
          <summary style={{ cursor: 'pointer', color: HUES.strategy.text }}>Copy as sent to {teamName}</summary>
          {spec.copy_of_record_html ? (
            <div style={{ fontFamily: FONTS.body, color: INK.primary, background: NEUTRAL.pageBg, border: `1px solid ${NEUTRAL.borderSoft}`, borderRadius: RADII.small, padding: '10px 12px', margin: '6px 0 0', maxHeight: '220px', overflowY: 'auto', lineHeight: 1.5 }}
              dangerouslySetInnerHTML={{ __html: sanitizeCopyHtml(spec.copy_of_record_html) }} />
          ) : (
            <pre style={{ whiteSpace: 'pre-wrap', fontFamily: FONTS.body, background: NEUTRAL.pageBg, border: `1px solid ${NEUTRAL.borderSoft}`, borderRadius: RADII.small, padding: '10px 12px', margin: '6px 0 0', maxHeight: '220px', overflowY: 'auto' }}>
              {spec.copy_of_record}
            </pre>
          )}
        </details>
      )}

      {canCorrect && correcting && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {failures.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px', background: STATE.danger.tint, border: `1px solid ${STATE.danger.border}`, borderRadius: RADII.small, padding: '8px 10px' }}>
              <div style={{ fontWeight: 600, color: STATE.danger.deep }}>
                Check findings — untick anything that isn't really a problem:
              </div>
              {failures.map(f => (
                <label key={f.id} style={{ display: 'flex', gap: '6px', alignItems: 'flex-start', cursor: 'pointer', color: INK.primary, lineHeight: 1.45 }}>
                  <input type="checkbox" checked={tickedFailures.includes(f.id)}
                    onChange={e => setTickedFailures(t => e.target.checked ? [...t, f.id] : t.filter(x => x !== f.id))} />
                  <span><b>{f.label}</b>{f.detail ? ` — ${f.detail}` : ''}</span>
                </label>
              ))}
            </div>
          )}
          <textarea value={note} onChange={e => setNote(e.target.value)} rows={3}
            placeholder={failures.length ? 'Anything else to add? (optional)' : `What needs to change on this page? ${team || 'The build team'} sees exactly this.`}
            style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', fontSize: '12.5px', fontFamily: FONTS.body, border: `1px solid ${STATE.gate.border}`, borderRadius: RADII.small, background: STATE.gate.bg }} />
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={sendCorrections} disabled={busy}
              style={buttonPrimarySmall(STATE.gate.main)}>Send corrections to {teamName}</button>
            <button onClick={() => { setCorrecting(false); setNote(''); setErr(''); }} style={buttonSmall}>Cancel</button>
          </div>
        </div>
      )}
      {!correcting && (actionable || (canCorrect && b.status === 'accepted') || (b.url && !launched && b.status !== 'accepted')) && (
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {actionable && (
            <button onClick={accept} disabled={busy}
              style={buttonPrimarySmall(STATE.done.main)}>✓ Accept page</button>
          )}
          {actionable && (
            <button onClick={openComposer} style={buttonSmall}>Request corrections…</button>
          )}
          {canCorrect && b.status === 'accepted' && (
            <button onClick={openComposer} style={buttonSmall} title="Reopen this accepted page — possible any time before launch">
              ↺ Reopen for corrections…
            </button>
          )}
          {b.url && !launched && (
            <button onClick={runCheck} disabled={checking} style={buttonSmall}>
              {checking ? 'Checking…' : '↻ Run check'}
            </button>
          )}
        </div>
      )}
      {b.status === 'corrections_requested' && (
        <div style={{ fontSize: '12px', color: STATE.gate.text }}>
          Waiting on {teamName} to fix and resubmit.
        </div>
      )}
      {err && <div style={{ fontSize: '12px', color: STATE.danger.text }}>{err}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Phase 4 — Approve & launch (Edward's framing, settled 2026-10-08): the
// PACKAGE moves as one from here — complete → to the client → launch →
// live. Milestones are timestamped project facts; the computed stage pulls
// the display back on its own if a page reopens. Client approval is the
// normal step (skippable when an engagement doesn't need it).
// ---------------------------------------------------------------------------

function MilestoneDone({ m, label, onUndo }) {
  return (
    <span style={{ fontSize: '12px', color: STATE.done.text, fontWeight: 600 }}>
      ✓ {label} {m?.at ? fmtDate(m.at) : ''}
      {onUndo && (
        <button onClick={onUndo} title="Undo — clears this and every later milestone"
          style={{ border: 'none', background: 'none', color: INK.muted, cursor: 'pointer', fontSize: '11px' }}>✕</button>
      )}
    </span>
  );
}

export function ApproveLaunchPanel({ projectId, deploy, onReload, onOpenBuild }) {
  const [emailDraft, setEmailDraft] = useState(null); // {subject, body} | null
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const ms = deploy?.milestones || {};
  const launched = !!ms.launched;

  const stamp = async (milestone, confirmText) => {
    if (confirmText && !window.suppressConfirm && !window.confirm(confirmText)) return;
    setBusy(true); setErr('');
    try { await axios.post(`${API}/contentiq/projects/${projectId}/milestones/${milestone}`); await onReload(); }
    catch (e) { setErr(e.response?.data?.detail || 'Could not record that.'); }
    finally { setBusy(false); }
  };
  const undo = async (milestone) => {
    if (!window.suppressConfirm && !window.confirm('Undo this milestone? It clears this step and every later one.')) return;
    setBusy(true); setErr('');
    try { await axios.delete(`${API}/contentiq/projects/${projectId}/milestones/${milestone}`); await onReload(); }
    catch (e) { setErr(e.response?.data?.detail || 'Could not undo that.'); }
    finally { setBusy(false); }
  };
  const downloadPacket = async () => {
    setErr('');
    try {
      await downloadFileBlob(`${API}/contentiq/projects/${projectId}/outputs/approval-packet`,
        'Landing Pages for Approval.docx');
    } catch { setErr('Could not build the approval packet.'); }
  };
  const loadEmailDraft = async () => {
    if (emailDraft) { setEmailDraft(null); return; }
    setErr('');
    try {
      const r = await axios.get(`${API}/contentiq/projects/${projectId}/outputs/approval-packet/email-draft`);
      setEmailDraft(r.data);
    } catch (e) { setErr(e.response?.data?.detail || 'Could not build the email draft.'); }
  };
  const copyDraft = async () => {
    try {
      await navigator.clipboard.writeText(`Subject: ${emailDraft.subject}\n\n${emailDraft.body}`);
      setCopied(true); setTimeout(() => setCopied(false), 2500);
    } catch { /* clipboard unavailable — the text is on screen to copy by hand */ }
  };

  const builds = deploy?.builds || [];
  const total = builds.length;
  const accepted = builds.filter(b => b.status === 'accepted').length;
  const allAccepted = !!deploy?.rollup?.all_accepted && total > 0;

  const stepNo = (n, active, done) => (
    <div style={{
      width: '26px', height: '26px', borderRadius: RADII.pill, flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: '13px', fontWeight: 700,
      background: done ? STATE.done.main : active ? HUES.strategy.main : NEUTRAL.band,
      color: done || active ? NEUTRAL.pageBg : INK.muted,
    }}>{done ? '✓' : n}</div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', fontFamily: FONTS.body }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div style={{ ...displayFont('22px', 700), letterSpacing: '-0.2px' }}>Approve &amp; launch</div>
        <CiqStageChip stage={deploy?.stage} />
      </div>
      <div style={{ fontSize: '12.5px', color: INK.secondary }}>
        The package moves as one from here — to the client, then to launch.
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxWidth: '780px' }}>

        {/* 1 — the package must be complete */}
        <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
          {stepNo(1, !allAccepted, allAccepted)}
          <div style={{
            flexGrow: 1, borderRadius: RADII.control, padding: '12px 16px',
            border: `1px solid ${allAccepted ? STATE.done.border : NEUTRAL.borderSoft}`,
            background: allAccepted ? STATE.done.tint : NEUTRAL.pageBg,
          }}>
            <div style={{ fontSize: '13.5px', fontWeight: 700, color: allAccepted ? STATE.done.deep : INK.primary }}>
              {allAccepted
                ? (total === 1 ? 'Package complete — the page is accepted'
                  : `Package complete — all ${total} pages accepted`)
                : total > 0
                  ? `Package not complete yet — ${accepted} of ${total} page${total === 1 ? '' : 's'} accepted`
                  : 'No pages with the build team yet'}
            </div>
            <div style={{ fontSize: '12px', color: allAccepted ? STATE.done.text : INK.secondary, lineHeight: 1.5 }}>
              {allAccepted
                ? 'One page needs a change? Reopen it in Page build — the package pauses here until it is whole again.'
                : 'Everything below unlocks when the whole package is accepted.'}
              {onOpenBuild && (
                <button onClick={onOpenBuild} style={{ ...buttonLink, fontSize: '12px', paddingLeft: '6px' }}>
                  ← Back to Page build
                </button>
              )}
            </div>
          </div>
        </div>

        {/* 2 — client approval of the package */}
        <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start', opacity: allAccepted || ms.packet_sent || ms.client_approved ? 1 : 0.55 }}>
          {stepNo(2, allAccepted && !ms.client_approved && !launched, !!ms.client_approved)}
          <div style={{ flexGrow: 1, border: `1px solid ${NEUTRAL.borderSoft}`, borderRadius: RADII.control, padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={{ fontSize: '13.5px', fontWeight: 700 }}>
              Client approval of the package
              <span style={{ fontWeight: 400, color: INK.muted }}> — skippable when an engagement doesn't need it</span>
            </div>
            <div style={{ fontSize: '12px', color: INK.secondary, lineHeight: 1.5 }}>
              The live links go to the client as one bundle: download the packet (every
              page's title and live link), or open the ready-made email in your own mail
              app. You send it yourself — nothing is ever auto-mailed.
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
              <button onClick={downloadPacket} disabled={!allAccepted} style={{ ...buttonSmall, ...(!allAccepted ? buttonDisabled : {}) }}>⬇ Approval packet (docx)</button>
              <button onClick={loadEmailDraft} disabled={!allAccepted} style={{ ...buttonSmall, ...(!allAccepted ? buttonDisabled : {}) }}>{emailDraft ? 'Hide email draft' : '✉ Email draft'}</button>
              {ms.packet_sent
                ? <MilestoneDone m={ms.packet_sent} label="Sent to client" onUndo={launched ? null : () => undo('packet_sent')} />
                : <button onClick={() => stamp('packet_sent')} disabled={busy || launched || !allAccepted} style={{ ...buttonSmall, ...(busy || launched || !allAccepted ? buttonDisabled : {}) }}>Mark packet sent</button>}
              {ms.client_approved
                ? <MilestoneDone m={ms.client_approved} label="Client approved" onUndo={launched ? null : () => undo('client_approved')} />
                : <button onClick={() => stamp('client_approved')} disabled={busy || launched || !allAccepted} style={{ ...buttonSmall, ...(busy || launched || !allAccepted ? buttonDisabled : {}) }}>Mark client approved</button>}
            </div>
            {/* The corrections loop, spelled out where the PM is when the
                client answers (Edward, 2026-10-09: "get approval or
                corrections, which would then need to go back to the partner
                for edits"). Reopening lives on each page card in Page build;
                this is the signposted road there. */}
            {!launched && (
              <div style={{ fontSize: '12px', color: INK.muted, lineHeight: 1.5 }}>
                Client asked for changes instead? Reopen just those pages in Page build —
                they go back to the build team for edits, and the package returns here
                once it is whole again.
                {onOpenBuild && (
                  <button onClick={onOpenBuild}
                    style={{ ...buttonLink, fontSize: '12px', paddingLeft: '6px' }}>
                    Go to Page build to reopen pages →
                  </button>
                )}
              </div>
            )}
            {emailDraft && (
              <div style={{ fontSize: '12px', background: NEUTRAL.pageBg, border: `1px solid ${NEUTRAL.borderSoft}`, borderRadius: RADII.small, padding: '10px 12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                  <b style={{ color: INK.primary }}>Subject: {emailDraft.subject}</b>
                  <div style={{ flexGrow: 1 }} />
                  {/* mailto: opens the PM's OWN mail client with the draft —
                      nothing is sent from here (the no-auto-mail rule). */}
                  <a href={`mailto:?subject=${encodeURIComponent(emailDraft.subject)}&body=${encodeURIComponent(emailDraft.body)}`}
                    style={{ ...buttonSmall, textDecoration: 'none', display: 'inline-block' }}>
                    ✉ Open in your mail app
                  </a>
                  <button onClick={copyDraft} style={buttonSmall}>{copied ? '✓ Copied' : '📋 Copy'}</button>
                </div>
                <pre style={{ whiteSpace: 'pre-wrap', fontFamily: FONTS.body, margin: 0, color: INK.primary, lineHeight: 1.5 }}>{emailDraft.body}</pre>
                <div style={{ color: INK.muted, marginTop: '6px' }}>
                  Paste into your own email — nothing is ever auto-mailed to a client.
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 3 — tell the build team to launch */}
        <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start', opacity: allAccepted || ms.launch_requested ? 1 : 0.55 }}>
          {stepNo(3, allAccepted && !ms.launch_requested, !!ms.launch_requested)}
          <div style={{ flexGrow: 1, border: `1px solid ${NEUTRAL.borderSoft}`, borderRadius: RADII.control, padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={{ fontSize: '13.5px', fontWeight: 700 }}>Tell {deploy?.team_name || 'the build team'} to launch</div>
            <div style={{ fontSize: '12px', color: INK.secondary, lineHeight: 1.5 }}>
              {deploy?.team_name || 'The build team'} gets a "ready to launch" notice and takes the campaign live.
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
              {ms.launch_requested
                ? <MilestoneDone m={ms.launch_requested} label="Launch requested" onUndo={launched ? null : () => undo('launch_requested')} />
                : (
                  <button onClick={() => stamp('launch_requested',
                    `Request launch? ${deploy?.team_name || 'The build team'} is notified that these pages are ready to go live.`)}
                    disabled={busy || !allAccepted} style={buttonPrimarySmall()}>
                    🚀 Request launch
                  </button>
                )}
              {ms.launch_requested && !launched && (
                <>
                  <span style={{ fontSize: '12px', color: INK.muted }}>
                    Waiting on {deploy?.team_name || 'the build team'} to mark it launched —
                  </span>
                  <button onClick={() => stamp('launched', `Mark launched? Use this when ${deploy?.team_name || 'the build team'} confirmed it outside the app.`)}
                    disabled={busy} style={buttonSmall}>or mark launched yourself</button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* 4 — launched */}
        <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start', opacity: launched ? 1 : 0.55 }}>
          {stepNo(4, false, launched)}
          <div style={{
            flexGrow: 1, borderRadius: RADII.control, padding: '12px 16px',
            border: launched ? `1px solid ${STATE.done.border}` : `1px dashed ${NEUTRAL.borderSoft}`,
            background: launched ? STATE.done.tint : NEUTRAL.pageBg,
          }}>
            <div style={{ fontSize: '13.5px', fontWeight: 700, color: launched ? STATE.done.deep : INK.secondary }}>
              {launched ? '🚀 Launched' : 'Launched'}
            </div>
            <div style={{ fontSize: '12px', color: launched ? STATE.done.text : INK.muted, lineHeight: 1.5 }}>
              {launched
                ? 'The campaign is live. Page changes after launch are a later phase — undo the Launched milestone first if something must move now.'
                : `${deploy?.team_name || 'The build team'} marks it live from their queue — or you can, if word comes by email.`}
            </div>
            {launched && (
              <div style={{ marginTop: '4px' }}>
                <MilestoneDone m={ms.launched} label="Launched" onUndo={() => undo('launched')} />
              </div>
            )}
          </div>
        </div>

      </div>
      {err && <div style={{ fontSize: '12px', color: STATE.danger.text }}>{err}</div>}
    </div>
  );
}

// Re-assign the whole page set to a different supplier — the wrong-person
// fix (Edward, 2026-10-08). Specs stay frozen, page statuses carry over;
// the new supplier is notified like a fresh assignment.
function ReassignControl({ projectId, onReload }) {
  const [open, setOpen] = useState(false);
  const [suppliers, setSuppliers] = useState(null);
  const [supplierId, setSupplierId] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!open || suppliers !== null) return;
    axios.get(`${API}/suppliers/`).then(r => setSuppliers(r.data)).catch(() => setSuppliers([]));
  }, [open, suppliers]);

  const reassign = async () => {
    if (!supplierId) { setErr('Pick the build team these pages should move to.'); return; }
    const name = (suppliers || []).find(s => String(s.id) === String(supplierId))?.supplier_name || 'that supplier';
    if (!window.suppressConfirm && !window.confirm(
      `Move every page on this project to ${name}? They are notified, and the previous build team's queue empties.`)) return;
    setBusy(true); setErr('');
    try {
      await axios.post(`${API}/contentiq/projects/${projectId}/builds/reassign`, {
        supplier_id: Number(supplierId),
        note: note.trim() || null,
      });
      setOpen(false); setNote(''); setSupplierId('');
      await onReload();
    } catch (e) { setErr(e.response?.data?.detail || 'Could not re-assign the pages.'); }
    finally { setBusy(false); }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} style={{ ...buttonLink, fontSize: '12px' }}>
        ⇄ Re-assign the package…
      </button>
    );
  }
  return (
    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', fontSize: '12.5px' }}>
      <select value={supplierId} onChange={e => setSupplierId(e.target.value)}
        style={{ padding: '7px 10px', fontSize: '12.5px', fontFamily: FONTS.body, border: `1px solid ${NEUTRAL.controlSoft}`, borderRadius: RADII.small, background: NEUTRAL.pageBg }}>
        <option value="">— move the package to —</option>
        {(suppliers || []).map(s => <option key={s.id} value={s.id}>{s.supplier_name}</option>)}
      </select>
      <input value={note} onChange={e => setNote(e.target.value)} placeholder="Note to the new team (optional)"
        style={{ flexGrow: 1, minWidth: '200px', padding: '7px 10px', fontSize: '12px', fontFamily: FONTS.body, border: `1px solid ${NEUTRAL.controlSoft}`, borderRadius: RADII.small }} />
      <button onClick={reassign} disabled={busy} style={buttonPrimarySmall()}>Re-assign</button>
      <button onClick={() => { setOpen(false); setErr(''); }} style={buttonSmall}>Cancel</button>
      {err && <div style={{ width: '100%', fontSize: '12px', color: STATE.danger.text }}>{err}</div>}
    </div>
  );
}

// One collapsible block of "what the build team works from" (Edward,
// 2026-10-22: page copy and brand moved into Page build). Collapsed content
// stays mounted, so an unsaved brand edit survives a collapse.
function PrepSection({ id, title, hint, open, onToggle, children }) {
  return (
    <section id={id} style={{ ...card, padding: 0 }}>
      <button onClick={onToggle} aria-expanded={open}
        style={{ width: '100%', display: 'flex', alignItems: 'baseline', gap: '10px', padding: '12px 18px',
          background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', fontFamily: FONTS.body }}>
        <span aria-hidden="true" style={{ fontSize: '12px', color: INK.secondary, width: '10px' }}>{open ? '▾' : '▸'}</span>
        <span style={{ fontSize: '14px', fontWeight: 700, color: INK.primary }}>{title}</span>
        {hint && <span style={{ fontSize: '12px', color: INK.secondary }}>{hint}</span>}
      </button>
      <div style={{ display: open ? 'block' : 'none', padding: '0 18px 16px' }}>{children}</div>
    </section>
  );
}

// The registration fields as two lists — what the visitor fills in, and the
// hidden fields the page passes along (Edward, 2026-10-23: "Marketo Campaign
// Name" showed up as a form field). The server sorts them; the PM can move
// any one across. Saved on the project (manual_input.form_field_placement)
// and frozen into each page at the send — after it the lists are read-only
// and a change goes to the build team as a correction on the page.
function FormFieldPlacement({ fields, sent, onSave }) {
  const [busyKey, setBusyKey] = useState(null);
  const [err, setErr] = useState('');
  const { visible, hidden } = splitFormFields(fields);
  const move = async (field, placement) => {
    setBusyKey(field.key); setErr('');
    const next = Object.fromEntries(fields.map(f => [f.key, f.placement || 'visible']));
    next[field.key] = placement;
    try { await onSave(next); } catch { setErr('Could not move that field.'); }
    finally { setBusyKey(null); }
  };
  const column = (heading, hint, list, target, label) => (
    <div style={{ flex: '1 1 260px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <div style={{ fontSize: '12.5px', fontWeight: 700, color: INK.primary }}>{heading}</div>
      <div style={{ fontSize: '11.5px', color: INK.secondary, marginBottom: '2px' }}>{hint}</div>
      {list.length === 0 && <div style={{ fontSize: '12px', color: INK.muted }}>None.</div>}
      {list.map(f => (
        <div key={f.key} style={{ display: 'flex', alignItems: 'baseline', gap: '8px', fontSize: '12.5px' }}>
          <span style={{ color: INK.body }}>
            {f.client_alias || f.label}
            {f.tier === 'optional' && <span style={{ color: INK.muted }}> (optional)</span>}
          </span>
          {!sent && (
            <button onClick={() => move(f, target)} disabled={!!busyKey}
              style={{ ...buttonLink, fontSize: '11.5px', ...(busyKey ? { opacity: 0.5 } : {}) }}>
              {busyKey === f.key ? 'Moving…' : label}
            </button>
          )}
        </div>
      ))}
    </div>
  );
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '18px' }}>
        {column('Fields the visitor fills in', 'shown on the registration form', visible, 'hidden', 'Make hidden')}
        {column('Hidden fields the page passes along', 'campaign and tracking values the visitor never sees', hidden, 'visible', 'Show on the form')}
      </div>
      {sent && (
        <div style={{ fontSize: '12px', color: INK.secondary }}>
          These went out with the pages. To change one now, request corrections on the page and say which field moves.
        </div>
      )}
      {err && <div style={{ fontSize: '12px', color: STATE.danger.text }}>{err}</div>}
    </div>
  );
}

// The privacy link, added right where the gate asks for it (Edward,
// 2026-10-23: the gate sent PMs to another step and then stayed stale, so a
// first-timer looped between the two). Saving reloads the gate in place.
function PrivacyLinkInline({ onSave }) {
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const save = async () => {
    const v = url.trim();
    if (!/^https?:\/\/\S+\.\S+/.test(v)) { setErr('Enter the full link, starting with https://'); return; }
    setBusy(true); setErr('');
    try { await onSave(v); } catch { setErr('Could not save the link.'); }
    finally { setBusy(false); }
  };
  return (
    <span style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginTop: '8px' }}>
      <input value={url} onChange={e => setUrl(e.target.value)} placeholder="https://client.com/privacy"
        aria-label="Client privacy policy URL"
        style={{ padding: '7px 10px', fontSize: '13px', fontFamily: FONTS.body, border: `1px solid ${NEUTRAL.controlSoft}`,
          borderRadius: RADII.small, background: NEUTRAL.pageBg, flex: '1 1 280px', minWidth: 0 }} />
      <button onClick={save} disabled={busy || !url.trim()}
        style={{ ...buttonPrimarySmall(), ...(busy || !url.trim() ? buttonDisabled : {}) }}>Save link</button>
      {err && <span style={{ fontSize: '12px', color: STATE.danger.text }}>{err}</span>}
    </span>
  );
}

export default function DeploymentPanel({ projectId, deploy, onReload, onOpenLaunch, onOpenQuestions, onSavePrivacy,
                                          onSaveFieldPlacement, copySection, brandSection }) {
  const [openOverride, setOpenOverride] = useState({});
  const gate = deploy?.gate;
  const builds = deploy?.builds || [];
  const rollup = deploy?.rollup;

  if (!deploy) {
    return <div style={{ fontSize: '13px', color: INK.muted, fontFamily: FONTS.body }}>Loading Page build…</div>;
  }

  const sent = builds.length > 0;
  const launched = !!deploy?.milestones?.launched;
  const pageCount = builds.filter(b => !isEdmBuild(b)).length;
  const hasEdm = builds.some(isEdmBuild);
  const pkgAccepted = builds.filter(b => b.status === 'accepted').length;
  const pkgReview = builds.filter(b => IN_REVIEW_STATUSES.includes(b.status)).length;
  const pkgBuilding = builds.length - pkgAccepted - pkgReview;

  // Before the send these sections are the work, so they start open; after
  // it they are reference, so they start closed.
  const isOpen = (key) => (openOverride[key] !== undefined ? openOverride[key] : !sent);
  const toggle = (key) => setOpenOverride(o => ({ ...o, [key]: !isOpen(key) }));
  const openBrand = () => {
    setOpenOverride(o => ({ ...o, brand: true }));
    setTimeout(() => {
      const el = document.getElementById('ciq-brand-section');
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  };

  const prep = (
    <>
      {copySection && (
        <PrepSection id="ciq-copy-section" title="Page copy" open={isOpen('copy')} onToggle={() => toggle('copy')}
          hint="what the build team receives, page by page">
          {copySection}
        </PrepSection>
      )}
      {brandSection && (
        <PrepSection id="ciq-brand-section" title="Brand" open={isOpen('brand')} onToggle={() => toggle('brand')}
          hint="logos, colors, never-use words and the legal line">
          {brandSection}
        </PrepSection>
      )}
      {onSaveFieldPlacement && (
        <PrepSection id="ciq-form-fields-section" title="Form fields" open={isOpen('fields')} onToggle={() => toggle('fields')}
          hint="what the visitor fills in, and what the page passes along hidden">
          {(deploy?.form_fields || []).length > 0 ? (
            <FormFieldPlacement fields={deploy.form_fields} sent={sent} onSave={onSaveFieldPlacement} />
          ) : (
            // No Required Fields on the program: the page falls back to
            // generic firmographic fields (the 2026-10-28 walkthrough asked
            // where the real ones come from).
            <div style={{ fontSize: '12.5px', color: INK.body, lineHeight: 1.6 }}>
              This program has no form fields set, so the page uses the standard firmographic
              fields (name, title, company, industry). To use the client’s own fields, set them in{' '}
              <a href="/qa-rules" target="_blank" rel="noopener noreferrer" style={{ color: BRAND.link, fontWeight: 600 }}>
                QA Rules → Required Fields
              </a>{' '}for this program, then come back here.
            </div>
          )}
        </PrepSection>
      )}
      <BuildIngredientsCard projectId={projectId} onOpenBrand={brandSection ? openBrand : undefined}
        onOpenQuestions={onOpenQuestions} refreshToken={deploy} />
    </>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', fontFamily: FONTS.body }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div style={{ ...displayFont('22px', 700), letterSpacing: '-0.2px' }}>Page build</div>
        <CiqStageChip stage={deploy?.stage} />
      </div>

      {!sent && gate?.ready && (
        <div role="status" style={{ ...card, padding: '12px 18px', display: 'flex', alignItems: 'center', gap: '12px',
          flexWrap: 'wrap', background: STATE.done.tint, border: `1px solid ${STATE.done.border}` }}>
          <span style={{ flex: '1 1 280px', fontSize: '13px', color: STATE.done.deep }}>
            <b>Ready to send.</b> The page copy is final — check the brand and images below, then send the package.
          </span>
          <button onClick={() => {
            const el = document.getElementById('ciq-package-send');
            if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }} style={buttonPrimarySmall()}>Go to Send the package ↓</button>
        </div>
      )}

      {!sent && prep}

      {!sent && !gate?.ready && (
        <div style={{ ...card, padding: '14px 18px', fontSize: '13px', color: INK.secondary, lineHeight: 1.6 }}>
          <b style={{ color: INK.primary }}>Not ready for the build team yet.</b><br />
          {/* The link field sits right here, so drop the "go to the other
              step" sentence the server's reason carries. */}
          {/privacy/i.test(gate?.reason || '') && onSavePrivacy
            ? String(gate.reason).replace(/\s*Add it on the Questions & privacy step\.?\s*$/, ' Add it here:')
            : gate?.reason}
          {/privacy/i.test(gate?.reason || '') && onSavePrivacy && <PrivacyLinkInline onSave={onSavePrivacy} />}
          {/privacy/i.test(gate?.reason || '') && !onSavePrivacy && onOpenQuestions && (
            <> <button onClick={onOpenQuestions} style={buttonLink}>Go to Questions &amp; privacy →</button></>
          )}
        </div>
      )}

      {(gate?.ready || sent) && (
        <PackagePanel projectId={projectId} onReload={onReload} refreshToken={deploy} />
      )}

      {sent && (
        <>
          {/* Review progress across the pages: the package panel above says
              who has the ball; this says how many are back and whole.
              Pages stay below as the detail / the one-page-fix exception. */}
          <div style={{ ...card, padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: '9px' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap' }}>
              {/* The headline says where the package actually IS (Edward,
                  2026-10-09: "it's not actually with the builder, it's back
                  with me as reviewer"). */}
              <span style={{ ...displayFont('15px', 650) }}>
                The pages — {pageCount} landing page{pageCount === 1 ? '' : 's'}
                {hasEdm ? ' + promotion email' : ''}
                {rollup?.all_accepted
                  ? ' — complete'
                  : pkgReview > 0 && pkgBuilding === 0
                    ? ' — back for your review'
                    : pkgReview > 0
                      ? ` — ${pkgReview} of ${builds.length} back for your review`
                      : ` with ${deploy?.team_name || 'the build team'}`}
              </span>
              <div style={{ flexGrow: 1 }} />
              {!launched && <ReassignControl projectId={projectId} onReload={onReload} />}
            </div>
            <div style={{ display: 'flex', height: '9px', borderRadius: RADII.pill, overflow: 'hidden', background: NEUTRAL.border }}>
              {pkgAccepted > 0 && <div style={{ width: `${(pkgAccepted / builds.length) * 100}%`, background: STATE.done.main }} />}
              {pkgReview > 0 && <div style={{ width: `${(pkgReview / builds.length) * 100}%`, background: STATE.gate.strong }} />}
              {pkgBuilding > 0 && <div style={{ width: `${(pkgBuilding / builds.length) * 100}%`, background: HUES.strategy.dim }} />}
            </div>
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', fontSize: '12.5px', color: INK.body }}>
              <span>{rollup?.accepted || 0} of {rollup?.total || builds.length} accepted.</span>
              {pkgReview > 0 && <span style={{ color: STATE.gate.text, fontWeight: 600 }}>{pkgReview} for your review</span>}
              {pkgBuilding > 0 && <span style={{ color: HUES.strategy.text }}>{pkgBuilding} with {deploy?.team_name || 'the build team'}</span>}
            </div>
            {/* The next step must be unmissable once the package is whole —
                an inline text link wasn't (Edward, 2026-10-09: "not clear
                how I would take next steps"). */}
            {rollup?.all_accepted && onOpenLaunch ? (
              <button onClick={onOpenLaunch} style={{ ...buttonPrimary(), alignSelf: 'flex-start' }}>
                Continue to Approve &amp; launch →
              </button>
            ) : (
              <div style={{ fontSize: '12px', color: INK.secondary }}>
                Approve &amp; launch unlocks once every page is accepted.
              </div>
            )}
          </div>
          {builds.map(b => <BuildRow key={b.id} b={b} onReload={onReload} launched={launched} team={deploy?.team_name} />)}

          <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.5px', color: INK.muted, textTransform: 'uppercase', marginTop: '6px' }}>
            What the build team works from
          </div>
          {prep}
        </>
      )}
    </div>
  );
}
